import { useEffect, useState } from 'react';
import {
  Bot, CheckCircle2, Loader2, RefreshCw, AlertCircle, Check, ChevronRight,
} from 'lucide-react';
import { ContactPicker } from '@/components/ContactPicker';
import type {
  BauvorhabenDocument, Contact, ExtractionApplyPayload, ExtractionApplyResult,
  ExtractionBauherr, ExtractionBauherrAssignment, ExtractionJob, DatenblattFelder,
  DatenblattOptions,
} from '@/types';
import { DatenblattFelderEditor } from './BauvorhabenDatenblattCard';

const statusLabels: Record<ExtractionJob['status'], string> = {
  pending: 'Wartet auf Worker',
  processing: 'In Bearbeitung',
  done: 'Fertig',
  failed: 'Fehlgeschlagen',
};

const statusClasses: Record<ExtractionJob['status'], string> = {
  pending: 'bg-gray-100 text-gray-800',
  processing: 'bg-amber-100 text-amber-800',
  done: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-700',
};

const bauherrFieldLabels: { key: keyof ExtractionBauherr; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'strasse', label: 'Straße' },
  { key: 'plz', label: 'PLZ' },
  { key: 'ort', label: 'Ort' },
  { key: 'festnetz', label: 'Festnetz' },
  { key: 'mobil_1', label: 'Mobil' },
  { key: 'mobil_2', label: 'Mobil 2' },
  { key: 'email_1', label: 'E-Mail' },
  { key: 'email_2', label: 'E-Mail 2' },
];

const bauadresseFieldLabels = [
  { key: 'strasse' as const, label: 'Straße' },
  { key: 'plz' as const, label: 'PLZ' },
  { key: 'ort' as const, label: 'Ort' },
];

const modeLabels: Record<ExtractionBauherrAssignment['mode'], string> = {
  link: 'Bestehendem Kontakt zuordnen (Antrag)',
  create: 'Als neuen Kontakt vorschlagen (Antrag)',
  skip: 'Nicht übernehmen',
};

const inputClass =
  'w-full rounded border border-gray-200 px-2 py-1 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none disabled:bg-gray-50 disabled:text-gray-500';

function formatDateTime(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '';
}

/** Leerer Bauherr-Block fürs Review-Editing. */
function emptyBauherr(): ExtractionBauherr {
  return { name: null, strasse: null, plz: null, ort: null, festnetz: null, mobil_1: null, mobil_2: null, email_1: null, email_2: null };
}

/** Katalog-Felder aus dem (redigierten) Ergebnis — die Review-UI
 *  bearbeitet dieselbe Struktur, die apply() direkt übernimmt. */
function resultFelder(result: NonNullable<ExtractionJob['result']>): DatenblattFelder {
  return {
    gemarkung_flurstueck: result.gemarkung_flurstueck ?? null,
    erstkontakt_am: result.erstkontakt_am ?? null,
    vertragssumme_brutto: result.vertragssumme_brutto ?? null,
    haustyp: result.haustyp ?? null,
    vertrag_abgeschlossen_am: result.vertrag_abgeschlossen_am ?? null,
    wohnflaeche_m2: result.wohnflaeche_m2 ?? null,
    gesamtflaeche_m2: result.gesamtflaeche_m2 ?? null,
    effizienzstandard: result.effizienzstandard ?? null,
    bodengutachten: result.bodengutachten ?? 'offen',
    bodengutachten_von: result.bodengutachten_von ?? null,
    hoehennivellement: result.hoehennivellement ?? 'offen',
    hoehennivellement_von: result.hoehennivellement_von ?? null,
    bauantrag_extern: result.bauantrag_extern ?? false,
    bauantrag_extern_von: result.bauantrag_extern_von ?? null,
    baugenehmigung_vorhanden: result.baugenehmigung_vorhanden ?? false,
    kfw_baubegleitung: result.kfw_baubegleitung ?? 'offen',
    beauftragung_durch_bauherr: result.beauftragung_durch_bauherr ?? false,
    auftragsformular_unterzeichnet: result.auftragsformular_unterzeichnet ?? false,
    zusatzkostenblatt: result.zusatzkostenblatt ?? false,
    schallschutz: result.schallschutz ?? false,
    angebot_garage: result.angebot_garage ?? false,
    angebot_garage_von: result.angebot_garage_von ?? null,
    sonstige_angebote: result.sonstige_angebote ?? null,
    fremdmakler: result.fremdmakler ?? false,
    gebaut_von: result.gebaut_von ?? null,
    infoblaetter: result.infoblaetter ?? [],
    interessenten_quellen: result.interessenten_quellen ?? [],
    interessenten_hinweis: result.interessenten_hinweis ?? null,
    anmerkungen: result.anmerkungen ?? null,
  };
}

/**
 * KI-Extraktion am Bauvorhaben (Phase 2): Extraktion aus einem
 * hochgeladenen Datenblatt-PDF starten (Mac-mini-Worker + lokales
 * Ollama-Modell), Status pollen, Ergebnis im Review-Formular prüfen
 * und korrigieren — dann übernehmen. Datenblatt-Felder werden direkt
 * übernommen; Bauherr-Kontaktdaten erzeugen IMMER Anträge, die ein
 * Genehmiger freigibt („Übernehmen" nur Genehmiger+).
 */
export function BauvorhabenExtraktionCard({
  jobs, documents, bauherren, canStart, canApply,
  datenblattOptions, isStarting, startError, activeJob, applyResult, applyError,
  polling, pollCapped, onStart, onRefresh, onSelectJob, onApply, onDismissApplyResult,
}: {
  jobs: ExtractionJob[] | null;
  /** Dokumente fürs Start-Select (nur PDFs — der Worker liest PDF). */
  documents: BauvorhabenDocument[];
  /** Verknüpfte Bauherren des BVs (Default-Zuordnung im Review). */
  bauherren: { contact_id: number; name: string }[];
  canStart: boolean;
  /** Übernehmen = Genehmiger+ (schreibt Datenblatt/Adresse + Anträge). */
  canApply: boolean;
  datenblattOptions: DatenblattOptions | null;
  isStarting: boolean;
  startError: string;
  activeJob: ExtractionJob | null;
  applyResult: ExtractionApplyResult | null;
  applyError: string;
  /** Seite pollt den aktiven Job (pending/processing). */
  polling: boolean;
  /** 10-Minuten-Cap erreicht — manueller Refresh nötig. */
  pollCapped: boolean;
  onStart: (documentId: number) => void;
  onRefresh: () => void;
  onSelectJob: (job: ExtractionJob) => void;
  onApply: (jobId: number, payload: ExtractionApplyPayload) => Promise<void>;
  onDismissApplyResult: () => void;
}) {
  const pdfDocuments = documents.filter(
    (d) => (d.file_mime ?? '').includes('pdf') || (d.original_name ?? '').toLowerCase().endsWith('.pdf')
  );

  const [documentId, setDocumentId] = useState('');
  const [review, setReview] = useState<ExtractionJob['result']>(null);
  const [assignments, setAssignments] = useState<
    Partial<Record<'bauherr_1' | 'bauherr_2', ExtractionBauherrAssignment>>
  >({});
  const [isApplying, setIsApplying] = useState(false);

  // Review-State neu initialisieren, wenn ein anderer Job geöffnet wird
  // (oder derselbe Job frisch geladen wurde — Poll-Ergebnis übernimmt
  // dann bewusst den aktuellen Stand und verwirft lokale Edits).
  const resultKey = activeJob ? `${activeJob.id}-${activeJob.status}-${activeJob.finished_at ?? ''}` : '';
  useEffect(() => {
    setReview(activeJob?.result ? { ...activeJob.result } : null);
    if (activeJob?.result) {
      const hasBauherr1 = !!activeJob.result.bauherr_1;
      const hasBauherr2 = !!activeJob.result.bauherr_2;
      // Default: genau ein verknüpfter Bauherr → diesem zuordnen,
      // sonst als neuen Kontakt vorschlagen.
      const firstMode: ExtractionBauherrAssignment['mode'] =
        bauherren.length === 1 ? 'link' : 'create';
      setAssignments({
        bauherr_1: hasBauherr1 ? { mode: firstMode, contact_id: firstMode === 'link' ? bauherren[0].contact_id : undefined } : undefined,
        bauherr_2: hasBauherr2 ? { mode: 'create' } : undefined,
      });
    } else {
      setAssignments({});
    }
    // bauherren als Objekt-Referenz: stabil genug (neu geladen nur bei
    // loadDetail); resultKey trägt den eigentlichen Reload-Trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultKey]);

  const setFeld = (key: keyof DatenblattFelder, value: string | boolean | string[]) => {
    setReview((prev) => prev && ({ ...prev, [key]: value }) as NonNullable<ExtractionJob['result']>);
  };

  const setBauherrFeld = (
    block: 'bauherr_1' | 'bauherr_2',
    key: keyof ExtractionBauherr,
    value: string
  ) => {
    setReview((prev) => {
      if (!prev) return prev;
      const blockData = { ...(prev[block] ?? emptyBauherr()), [key]: value || null };
      return { ...prev, [block]: blockData };
    });
  };

  const setBauadresseFeld = (key: 'strasse' | 'plz' | 'ort', value: string) => {
    setReview((prev) => {
      if (!prev) return prev;
      const adresse = { ...(prev.bauadresse ?? { strasse: null, plz: null, ort: null }), [key]: value || null };
      return { ...prev, bauadresse: adresse };
    });
  };

  const handleApply = async () => {
    if (!activeJob || !review) return;
    setIsApplying(true);
    try {
      await onApply(activeJob.id, { result: review, assignments });
    } finally {
      setIsApplying(false);
    }
  };

  /** Ein Bauherr-Block im Review: 9 Felder + Zuordnungs-Modus. */
  const bauherrBlock = (blockKey: 'bauherr_1' | 'bauherr_2', title: string) => {
    const data = review?.[blockKey];
    if (!data) return null;
    const assignment = assignments[blockKey] ?? { mode: 'skip' as const };
    return (
      <div className="rounded-lg border border-gray-100 p-3">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-medium text-gray-900">{title}</span>
          <select
            value={assignment.mode}
            disabled={!canApply}
            onChange={(e) => {
              const mode = e.target.value as ExtractionBauherrAssignment['mode'];
              setAssignments((prev) => ({
                ...prev,
                [blockKey]: { mode, contact_id: mode === 'link' ? (prev[blockKey]?.contact_id ?? bauherren[0]?.contact_id) : undefined },
              }));
            }}
            className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-800 focus:border-primary-500 focus:outline-none disabled:bg-gray-50"
          >
            {(Object.keys(modeLabels) as ExtractionBauherrAssignment['mode'][]).map((mode) => (
              <option key={mode} value={mode}>{modeLabels[mode]}</option>
            ))}
          </select>
        </div>
        <div className="grid gap-x-4 gap-y-2 sm:grid-cols-2">
          {bauherrFieldLabels.map(({ key, label }) => (
            <div key={key}>
              <label className="mb-0.5 block text-xs font-medium text-gray-500">{label}</label>
              <input
                type="text"
                value={data[key] ?? ''}
                disabled={!canApply}
                onChange={(e) => setBauherrFeld(blockKey, key, e.target.value)}
                className={inputClass}
              />
            </div>
          ))}
        </div>
        {canApply && assignment.mode === 'link' && (
          <div className="mt-2">
            <p className="mb-1 text-xs font-medium text-gray-500">
              Kontakt für die Zuordnung
              {bauherren.length === 1 && ' (verknüpfter Bauherr vorselektiert)'}
            </p>
            <ContactPicker
              placeholder="Kontakt suchen…"
              onSelect={(contact: Contact) => {
                setAssignments((prev) => ({
                  ...prev,
                  [blockKey]: { mode: 'link', contact_id: contact.id },
                }));
              }}
            />
          </div>
        )}
        {!canApply && (
          <p className="mt-2 text-xs text-gray-400">
            Nur mit Genehmiger-Berechtigung übernehmbar — Kontaktdaten werden als Antrag eingereicht.
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="card p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Bot className="h-5 w-5 text-gray-400" />
          KI-Extraktion
        </h2>
        <button
          type="button"
          onClick={onRefresh}
          className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${polling ? 'animate-spin' : ''}`} />
          Aktualisieren
        </button>
      </div>

      {/* Start: Dokument wählen (nur PDFs) */}
      {canStart && (
        <div className="mb-5">
          {pdfDocuments.length === 0 ? (
            <p className="text-sm text-gray-500">
              Für eine Extraktion zuerst ein Datenblatt-PDF unter „Dokumente“ hochladen.
            </p>
          ) : (
            <div className="flex flex-col gap-2 sm:flex-row">
              <select
                value={documentId}
                onChange={(e) => setDocumentId(e.target.value)}
                className="input flex-1"
              >
                <option value="">Dokument wählen…</option>
                {pdfDocuments.map((doc) => (
                  <option key={doc.id} value={doc.id}>
                    {doc.title || doc.original_name}
                    {doc.category ? ` (${doc.category})` : ''}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={!documentId || isStarting}
                onClick={() => onStart(Number(documentId))}
                className="btn btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              >
                {isStarting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bot className="h-4 w-4" />}
                Extraktion starten
              </button>
            </div>
          )}
          {startError && (
            <p className="mt-2 flex items-center gap-1.5 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {startError}
            </p>
          )}
          <p className="mt-2 text-xs text-gray-400">
            Der Mac-mini-Worker prüft alle {'"'}20 Sekunden{'"'} nach offenen Jobs; gescannte
            Datenblätter (OCR) können einige Minuten dauern.
          </p>
        </div>
      )}

      {/* Job-Liste */}
      {jobs === null ? (
        <p className="text-sm text-gray-500">Lädt…</p>
      ) : jobs.length === 0 ? (
        <p className="text-sm text-gray-500">Noch keine Extraktionen für dieses Bauvorhaben.</p>
      ) : (
        <ul className="mb-5 divide-y divide-gray-100">
          {jobs.map((job) => (
            <li key={job.id}>
              <button
                type="button"
                onClick={() => onSelectJob(job)}
                className={`flex w-full items-center justify-between gap-2 py-2 text-left ${
                  activeJob?.id === job.id ? 'text-primary-700' : 'text-gray-900 hover:text-gray-600'
                }`}
              >
                <span className="flex min-w-0 items-center gap-2 text-sm">
                  <ChevronRight className="h-4 w-4 shrink-0 text-gray-300" />
                  <span className="truncate">{job.document_title || job.original_name}</span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  {job.applied_at && (
                    <span className="inline-flex items-center gap-1 text-xs text-green-700">
                      <Check className="h-3.5 w-3.5" /> übernommen
                    </span>
                  )}
                  <span className="text-xs text-gray-400">{formatDateTime(job.created_at)}</span>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClasses[job.status]}`}>
                    {statusLabels[job.status]}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Aktiver Job: Status / Review */}
      {activeJob && (
        <div className="rounded-lg border border-gray-200 p-4">
          <p className="mb-3 text-sm font-medium text-gray-900">
            {activeJob.document_title || activeJob.original_name}
          </p>

          {(activeJob.status === 'pending' || activeJob.status === 'processing') && (
            <p className="flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
              {pollCapped
                ? 'Die Extraktion dauert länger als 10 Minuten — bitte über „Aktualisieren“ neu prüfen.'
                : 'Extraktion läuft — Status wird automatisch alle paar Sekunden geprüft. OCR von Scans kann Minuten dauern…'}
            </p>
          )}

          {activeJob.status === 'failed' && (
            <p className="flex items-start gap-2 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {activeJob.error_message || 'Die Extraktion ist fehlgeschlagen.'}
            </p>
          )}

          {activeJob.status === 'done' && !review && !activeJob.applied_at && (
            <p className="text-sm text-gray-500">Kein Ergebnis gespeichert.</p>
          )}

          {/* Apply-Ergebnis (Zusammenfassung) */}
          {applyResult && (
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
              <p className="mb-1 flex items-center gap-1.5 font-medium">
                <CheckCircle2 className="h-4 w-4" />
                Extraktionsergebnis übernommen. Kontaktänderungen müssen genehmigt werden.
              </p>
              <ul className="list-inside list-disc text-xs">
                <li>{applyResult.datenblatt_fields_written} Datenblatt-Felder übernommen</li>
                {applyResult.bauadresse_applied && <li>Bauadresse aktualisiert</li>}
                {applyResult.address_proposals_created > 0 && (
                  <li>{applyResult.address_proposals_created} Adresse-Anträge für verknüpfte Bauherren</li>
                )}
                {applyResult.contact_proposal_ids.length > 0 && (
                  <li>{applyResult.contact_proposal_ids.length} Kontakt-Anträge eingereicht</li>
                )}
                {applyResult.skipped.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
              <button
                type="button"
                onClick={onDismissApplyResult}
                className="mt-2 text-xs underline hover:text-green-900"
              >
                Ausblenden
              </button>
            </div>
          )}
          {applyError && (
            <p className="mb-4 flex items-center gap-1.5 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {applyError}
            </p>
          )}

          {/* Review-Formular (editierbar nur für Genehmiger+) */}
          {activeJob.status === 'done' && review && (
            <div className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                {bauherrBlock('bauherr_1', 'Bauherr 1')}
                {bauherrBlock('bauherr_2', 'Bauherr 2')}
              </div>

              <div>
                <p className="mb-2 text-sm font-medium text-gray-900">Bauadresse</p>
                <div className="grid gap-x-4 gap-y-2 sm:grid-cols-3">
                  {bauadresseFieldLabels.map(({ key, label }) => (
                    <div key={key}>
                      <label className="mb-0.5 block text-xs font-medium text-gray-500">{label}</label>
                      <input
                        type="text"
                        value={review.bauadresse?.[key] ?? ''}
                        disabled={!canApply}
                        onChange={(e) => setBauadresseFeld(key, e.target.value)}
                        className={inputClass}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-2 text-sm font-medium text-gray-900">Datenblatt-Felder</p>
                {datenblattOptions ? (
                  <DatenblattFelderEditor
                    fields={resultFelder(review)}
                    options={datenblattOptions}
                    disabled={!canApply}
                    onChange={setFeld}
                  />
                ) : (
                  <p className="text-sm text-gray-500">
                    Datenblatt-Vokabulare konnten nicht geladen werden.
                  </p>
                )}
              </div>

              {canApply && !activeJob.applied_at && (
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-gray-500">
                    Datenblatt-Felder und Bauadresse werden direkt übernommen;
                    Bauherr-Kontaktdaten werden als Anträge eingereicht.
                  </p>
                  <button
                    type="button"
                    onClick={handleApply}
                    disabled={isApplying}
                    className="btn btn-primary inline-flex shrink-0 items-center gap-2 disabled:opacity-50"
                  >
                    {isApplying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    Übernehmen
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}